import React, { useEffect, useState } from 'react';
import {
  Grid, Card, TextField, Dialog, DialogActions, DialogContent,
  DialogTitle, Input, Box, IconButton, Switch, FormControlLabel,
  FormControl, InputLabel, Select, MenuItem, Chip, Tooltip,
  Divider, Typography, Accordion, AccordionSummary, AccordionDetails,
  Paper,
} from '@mui/material';
import {
  Add, Delete, Edit, ExpandMore, Tune, Close, Image as ImageIcon,
  Visibility,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import moment from 'moment';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import DataTable from 'examples/Tables/DataTable';
import MDBadge from 'components/MDBadge';
import MDButton from 'components/MDButton';
import { Axios } from 'services/axios';
import { Loading, Notify } from 'notiflix';
import { useNavigate } from 'react-router-dom';
import usePermission from 'hooks/usePermission';
import NoPermissionNotice from 'components/NoPermissionNotice';

const ATTRIBUTE_TYPES = ['string', 'number', 'boolean', 'enum', 'multi_enum'];

const Designs = () => {
  const canCreate = usePermission('shop-management', 'create');
  const canRead = usePermission('shop-management', 'read');
  const canUpdate = usePermission('shop-management', 'update');
  const canDelete = usePermission('shop-management', 'delete');
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [isActiveCategory, setIsActiveCategory] = useState(true);
  // Tracks whether admin wants to keep existing hero image or clear it
  const [keepHeroImage, setKeepHeroImage] = useState(true);
  const [keepThumbnail, setKeepThumbnail] = useState(true);

  // View category dialog
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [viewingCategory, setViewingCategory] = useState(null);

  // Attribute type states
  const [openAttrTypeDialog, setOpenAttrTypeDialog] = useState(false);
  const [attributeTypes, setAttributeTypes] = useState([]);
  const [selectedAttrType, setSelectedAttrType] = useState(null);
  const [openAttrOptionDialog, setOpenAttrOptionDialog] = useState(false);
  const [selectedAttrTypeForOptions, setSelectedAttrTypeForOptions] = useState(null);
  const [attrOptions, setAttrOptions] = useState([{ label: '', value: '', order: 1 }]);
  const [openManageAttrsDialog, setOpenManageAttrsDialog] = useState(false);
  const [managingCategory, setManagingCategory] = useState(null);

  const navigate = useNavigate();

  const {
    register: registerCategory,
    handleSubmit: handleSubmitCategory,
    reset: resetCategory,
    setValue: setValueCategory,
    formState: { errors: categoryErrors },
  } = useForm();

  const {
    register: registerAttrType,
    handleSubmit: handleSubmitAttrType,
    reset: resetAttrType,
    setValue: setValueAttrType,
    formState: { errors: attrTypeErrors },
  } = useForm({ defaultValues: { type: 'enum', isFilterable: true, isSearchable: false, orderInFilter: 0 } });

  // ===== CATEGORIES =====
  const fetchCategories = async () => {
    try {
      const response = await Axios.fetch('design/categoriesForAdmin');
      setCategories(response.data.data || []);
    } catch {
      Notify.failure('Failed to fetch categories');
    }
  };

  const handleCategorySubmit = async (data) => {
    Loading.circle('Processing...');
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('slug', data.slug);
    formData.append('description', data.description || '');
    formData.append('heading',     data.heading     || '');
    formData.append('subHeading',  data.subHeading  || '');
    formData.append('isActive', isActiveCategory);
    if (data.heroImage?.[0]) formData.append('heroImage', data.heroImage[0]);
    if (data.thumbnail?.[0])  formData.append('thumbnail',  data.thumbnail[0]);
    formData.append('keepHeroImage', keepHeroImage ? 'true' : 'false');
    formData.append('keepThumbnail',  keepThumbnail  ? 'true' : 'false');

    try {
      if (selectedCategory) {
        await Axios.put(`design/admin/categories/${selectedCategory.id}`, formData,
          { 'Content-Type': 'multipart/form-data' },
        );
        Notify.success('Category updated!');
      } else {
        await Axios.post('design/admin/categories', formData,
          { 'Content-Type': 'multipart/form-data' },
        ).then(res => Notify.success(res.data?.message || 'Category created!'));
      }
      closeCategoryDialog();
      fetchCategories();
    } catch (error) {
      Notify.failure(error.response?.data?.message || 'Failed to save category');
    } finally {
      Loading.remove();
    }
  };

  const handleEditCategory = async (categoryId) => {
    try {
      Loading.circle('Loading...');
      const response = await Axios.fetch(`design/admin/categories/${categoryId}`);
      const category = response.data.data;
      setSelectedCategory(category);
      setValueCategory('name', category.name || '');
      setValueCategory('slug', category.slug || '');
      setValueCategory('description', category.description || '');
      setValueCategory('heading',     category.heading     || '');
      setValueCategory('subHeading',  category.subHeading  || '');
      setIsActiveCategory(category.isActive ?? true);
      setKeepHeroImage(true);
    setKeepThumbnail(true); // always start with keep = true
      setKeepThumbnail(true);
      setOpenCategoryDialog(true);
    } catch {
      Notify.failure('Failed to load category');
    } finally {
      Loading.remove();
    }
  };

  const handleViewCategory = async (categoryId) => {
    try {
      Loading.circle('Loading...');
      const response = await Axios.fetch(`design/admin/categories/${categoryId}`);
      setViewingCategory(response.data.data);
      setOpenViewDialog(true);
    } catch {
      Notify.failure('Failed to load category');
    } finally {
      Loading.remove();
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Delete this category? All designs will be affected.')) return;
    Loading.circle('Deleting...');
    try {
      await Axios.delete(`design/category/${categoryId}`);
      Notify.success('Category deleted!');
      fetchCategories();
    } catch (error) {
      Notify.failure(error.response?.data?.message || 'Failed to delete category');
    } finally {
      Loading.remove();
    }
  };

  const closeCategoryDialog = () => {
    setOpenCategoryDialog(false);
    resetCategory();
    setSelectedCategory(null);
    setIsActiveCategory(true);
    setKeepHeroImage(true);
    setKeepThumbnail(true);
  };

  // ===== ATTRIBUTE TYPES =====
  const fetchAttributeTypes = async (categoryId) => {
    try {
      const response = await Axios.fetch(`design/admin/categories/${categoryId}/attribute-types`);
      setAttributeTypes(response.data.data || []);
    } catch {
      Notify.failure('Failed to load attribute types');
    }
  };

  const handleOpenManageAttrs = async (category) => {
    setManagingCategory(category);
    await fetchAttributeTypes(category.id);
    setOpenManageAttrsDialog(true);
  };

  const handleAttrTypeSubmit = async (data) => {
    Loading.circle('Saving...');
    try {
      if (selectedAttrType) {
        await Axios.put(`design/admin/attribute-types/${selectedAttrType.id}`, {
          name: data.name,
          isFilterable: data.isFilterable === 'true' || data.isFilterable === true,
          isSearchable: data.isSearchable === 'true' || data.isSearchable === true,
          orderInFilter: parseInt(data.orderInFilter) || 0,
        });
        Notify.success('Filter updated!');
      } else {
        await Axios.post(`design/admin/categories/${managingCategory.id}/attribute-types`, {
          key: data.key,
          name: data.name,
          type: data.type,
          isFilterable: true,
          isSearchable: data.isSearchable === 'true' || data.isSearchable === true,
          orderInFilter: parseInt(data.orderInFilter) || 0,
        });
        Notify.success('Filter created!');
      }
      closeAttrTypeDialog();
      fetchAttributeTypes(managingCategory.id);
    } catch (error) {
      Notify.failure(error.response?.data?.message || 'Failed to save filter');
    } finally {
      Loading.remove();
    }
  };

  const handleDeleteAttrType = async (attrTypeId) => {
    if (!window.confirm('Delete this filter? All item values for this filter will be lost.')) return;
    Loading.circle('Deleting...');
    try {
      await Axios.delete(`design/admin/attribute-types/${attrTypeId}`);
      Notify.success('Filter deleted!');
      fetchAttributeTypes(managingCategory.id);
    } catch (error) {
      Notify.failure(error.response?.data?.message || 'Failed to delete');
    } finally {
      Loading.remove();
    }
  };

  const closeAttrTypeDialog = () => {
    setOpenAttrTypeDialog(false);
    resetAttrType({ type: 'enum', isFilterable: true, isSearchable: false, orderInFilter: 0 });
    setSelectedAttrType(null);
  };

  // ===== ATTRIBUTE OPTIONS =====
  const handleOpenAddOptions = (attrType) => {
    setSelectedAttrTypeForOptions(attrType);
    setAttrOptions([{ label: '', value: '', order: 1 }]);
    setOpenAttrOptionDialog(true);
  };

  const handleOptionChange = (idx, field, value) => {
    setAttrOptions(prev => prev.map((opt, i) => i === idx ? { ...opt, [field]: value } : opt));
  };

  const handleAddOptionRow = () => {
    setAttrOptions(prev => [...prev, { label: '', value: '', order: prev.length + 1 }]);
  };

  const handleRemoveOptionRow = (idx) => {
    setAttrOptions(prev => prev.filter((_, i) => i !== idx));
  };

  const handleDeleteOption = async (optionId) => {
    if (!window.confirm('Delete this option?')) return;
    try {
      await Axios.delete(`design/admin/attribute-options/${optionId}`);
      Notify.success('Option deleted!');
      fetchAttributeTypes(managingCategory.id);
    } catch (error) {
      Notify.failure(error.response?.data?.message || 'Failed to delete option');
    }
  };

  const handleSubmitOptions = async () => {
    const valid = attrOptions.filter(o => o.label.trim() && o.value.trim());
    if (valid.length === 0) { Notify.warning('Add at least one option'); return; }
    Loading.circle('Adding options...');
    try {
      await Axios.post(
        `design/admin/attribute-types/${selectedAttrTypeForOptions.id}/options`,
        valid.map(o => ({ label: o.label.trim(), value: o.value.trim(), order: parseInt(o.order) || 1 }))
      );
      Notify.success(`${valid.length} option(s) added!`);
      setOpenAttrOptionDialog(false);
      fetchAttributeTypes(managingCategory.id);
    } catch (error) {
      Notify.failure(error.response?.data?.message || 'Failed to add options');
    } finally {
      Loading.remove();
    }
  };

  // ===== TABLE =====
  const categoriesColumns = [
    { Header: '#', accessor: 'sr', width: '4%' },
    { Header: 'Hero', accessor: 'hero', width: '8%' },
    { Header: 'Name', accessor: 'name', width: '22%' },
    { Header: 'Slug', accessor: 'slug', width: '15%' },
    { Header: 'Filters', accessor: 'filters', width: '14%' },
    { Header: 'Designs', accessor: 'designCount', width: '8%' },
    { Header: 'Status', accessor: 'status', width: '8%' },
    { Header: 'Created', accessor: 'createdOn', width: '10%' },
    { Header: 'Actions', accessor: 'action', width: '11%' },
  ];

  const getCategoryRow = (category, idx) => ({
    sr: <MDTypography variant="caption" fontWeight="medium">{idx + 1}</MDTypography>,
    hero: category.heroImage ? (
      <img
        src={category.heroImage}
        alt={category.name}
        style={{ width: 52, height: 38, objectFit: 'cover', borderRadius: 6, display: 'block', border: '1px solid #eee' }}
      />
    ) : (
      <Box sx={{
        width: 52, height: 38, borderRadius: '6px', bgcolor: 'grey.100',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: '1px dashed #ccc',
      }}>
        <ImageIcon sx={{ color: 'grey.400', fontSize: 16 }} />
      </Box>
    ),
    name: (
      <Box>
        <MDTypography
          variant="button" fontWeight="medium" display="block"
          sx={{ cursor: 'pointer', color: 'info.main', '&:hover': { textDecoration: 'underline' } }}
          onClick={() => navigate(`/admin/designs/category/${category.id}`)}
        >
          {category.name}
        </MDTypography>
        {category.description && (
          <MDTypography variant="caption" color="text"
            sx={{ opacity: 0.65, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180, display: 'block' }}>
            {category.description}
          </MDTypography>
        )}
      </Box>
    ),
    slug: <MDTypography variant="caption" sx={{ fontFamily: 'monospace', bgcolor: 'grey.100', px: 0.75, py: 0.25, borderRadius: 1 }}>{category.slug}</MDTypography>,
    filters: (
      <Tooltip title="Manage filters for this category">
        <Chip
          icon={<Tune fontSize="small" />}
          label="Filters"
          size="small"
          color="primary"
          variant="outlined"
          onClick={() => handleOpenManageAttrs(category)}
          sx={{ cursor: 'pointer' }}
        />
      </Tooltip>
    ),
    designCount: (
      <Chip
        label={category.designs?.length ?? '—'}
        size="small"
        variant="outlined"
        sx={{ cursor: 'pointer', fontWeight: 600 }}
        onClick={() => navigate(`/admin/designs/category/${category.id}`)}
      />
    ),
    status: (
      <MDBadge
        badgeContent={category.isActive ? 'Active' : 'Inactive'}
        color={category.isActive ? 'success' : 'secondary'}
        size="sm"
      />
    ),
    createdOn: <MDTypography variant="caption" color="text">{moment(category.createdAt).fromNow()}</MDTypography>,
    action: (
      <Box sx={{ display: 'flex', gap: 0.25 }}>
        {canRead && (
          <Tooltip title="View category">
            <IconButton size="small" onClick={() => handleViewCategory(category.id)} sx={{ color: 'success.main' }}>
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {canUpdate && (
          <Tooltip title="Edit category">
            <IconButton size="small" onClick={() => handleEditCategory(category.id)} sx={{ color: 'info.main' }}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {canDelete && (
          <Tooltip title="Delete category">
            <IconButton size="small" onClick={() => handleDeleteCategory(category.id)} sx={{ color: 'error.main' }}>
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    ),
  });

  useEffect(() => {
    if (canRead) fetchCategories();
  }, [canRead]);

  if (!canRead) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <NoPermissionNotice message="Your role doesn't include read access to shop management. Ask an administrator to update your permissions." />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2} mt={-3} py={3} px={2}
                variant="gradient" bgColor="info"
                borderRadius="lg" coloredShadow="info"
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <Box>
                  <MDTypography variant="h6" color="white">Design Categories</MDTypography>
                  <MDTypography variant="caption" color="white" sx={{ opacity: 0.8 }}>
                    {categories.length} categories
                  </MDTypography>
                </Box>
                {canCreate && (
                  <MDButton variant="gradient" color="white" startIcon={<Add />}
                    onClick={() => { setSelectedCategory(null); resetCategory(); setOpenCategoryDialog(true); }}>
                    Add Category
                  </MDButton>
                )}
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{
                    columns: categoriesColumns,
                    rows: categories.map((cat, idx) => getCategoryRow(cat, idx)),
                  }}
                  isSorted={false}
                  entriesPerPage={false}
                  hoverEffect
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* ===== VIEW CATEGORY DIALOG ===== */}
      {/* ===== VIEW CATEGORY DIALOG ===== */}
      <Dialog open={openViewDialog} onClose={() => setOpenViewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6">{viewingCategory?.name}</Typography>
              <Typography variant="caption" color="text.secondary">/{viewingCategory?.slug}</Typography>
            </Box>
            <IconButton size="small" onClick={() => setOpenViewDialog(false)}><Close /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {viewingCategory && (
            <Box>

              {/* ── Hero Image preview — as seen on the category browse page ── */}
              <MDTypography variant="button" fontWeight="medium" display="block" mb={1}>
                Hero Image
                <MDTypography variant="caption" color="text" component="span" sx={{ ml: 1, fontWeight: 400, fontSize: 10 }}>
                  Shown as full-width background on the category browse page
                </MDTypography>
              </MDTypography>
              {viewingCategory.heroImage ? (
                <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', mb: 2.5, height: 220 }}>
                  <img
                    src={viewingCategory.heroImage}
                    alt="Hero"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                  {/* Overlay + centred text — mirrors DesignBrowsePage hero */}
                  <Box sx={{
                    position: 'absolute', inset: 0,
                    background: 'linear-gradient(to bottom, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.62) 100%)',
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    textAlign: 'center', p: 2,
                  }}>
                    <MDTypography variant="h5" color="white" fontWeight="bold" mb={0.75}>
                      {viewingCategory.heading || viewingCategory.name}
                    </MDTypography>
                    {(viewingCategory.subHeading || viewingCategory.description) && (
                      <MDTypography variant="caption" color="white" sx={{ opacity: 0.9, maxWidth: 420 }}>
                        {viewingCategory.subHeading || viewingCategory.description}
                      </MDTypography>
                    )}
                  </Box>
                </Box>
              ) : (
                <Box sx={{
                  height: 80, border: '2px dashed', borderColor: 'grey.300',
                  borderRadius: 2, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', mb: 2.5, bgcolor: 'grey.50',
                }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <ImageIcon sx={{ color: 'grey.400', fontSize: 28, display: 'block', mx: 'auto' }} />
                    <MDTypography variant="caption" color="text" sx={{ opacity: 0.6 }}>No hero image uploaded</MDTypography>
                  </Box>
                </Box>
              )}

              <Divider sx={{ mb: 2.5 }} />

              {/* ── Thumbnail + info side by side ── */}
              <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexWrap: 'wrap' }}>

                {/* Thumbnail card preview — as seen on the main designs landing page */}
                <Box sx={{ flex: '0 0 auto' }}>
                  <MDTypography variant="button" fontWeight="medium" display="block" mb={1}>
                    Thumbnail Card
                    <MDTypography variant="caption" color="text" component="span" sx={{ ml: 1, fontWeight: 400, fontSize: 10 }}>
                      Shown on the main Designs page
                    </MDTypography>
                  </MDTypography>

                  {/* Mini simulation of the category card */}
                  <Box sx={{
                    width: 180, borderRadius: 2, overflow: 'hidden',
                    border: '1px solid', borderColor: 'grey.200', bgcolor: '#1a1a2e',
                    position: 'relative',
                  }}>
                    {/* Image */}
                    <Box sx={{ height: 160, overflow: 'hidden', position: 'relative' }}>
                      {viewingCategory.thumbnail ? (
                        <img
                          src={viewingCategory.thumbnail}
                          alt="Thumbnail"
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                      ) : (
                        <Box sx={{
                          width: '100%', height: '100%',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          bgcolor: 'grey.800',
                        }}>
                          <ImageIcon sx={{ color: 'grey.600', fontSize: 32 }} />
                        </Box>
                      )}
                      {/* gradient overlay like real card */}
                      <Box sx={{
                        position: 'absolute', inset: 0,
                        background: 'linear-gradient(to top, rgba(42,40,47,0.85) 0%, rgba(42,40,47,0) 55%)',
                      }} />
                    </Box>
                    {/* Bottom bar */}
                    <Box sx={{ p: 1.25 }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#fff', lineHeight: 1.3, mb: 0.5 }}>
                        {viewingCategory.name}
                      </Typography>
                      <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,0.65)', lineHeight: 1.4 }}>
                        {viewingCategory.description
                          ? viewingCategory.description.slice(0, 50) + (viewingCategory.description.length > 50 ? '…' : '')
                          : 'Explore templates'}
                      </Typography>
                    </Box>
                  </Box>
                  {!viewingCategory.thumbnail && (
                    <MDTypography variant="caption" color="warning" display="block" mt={0.75} sx={{ fontSize: 10 }}>
                      No thumbnail — hero image will be used as fallback
                    </MDTypography>
                  )}
                </Box>

                {/* Meta info */}
                <Box sx={{ flex: 1, minWidth: 180 }}>
                  <MDTypography variant="button" fontWeight="medium" display="block" mb={1.5}>Details</MDTypography>
                  <Grid container spacing={1.5}>
                    <Grid item xs={6}>
                      <MDTypography variant="caption" color="text" display="block">Status</MDTypography>
                      <MDBadge badgeContent={viewingCategory.isActive ? 'Active' : 'Inactive'}
                        color={viewingCategory.isActive ? 'success' : 'secondary'} size="sm" />
                    </Grid>
                    <Grid item xs={6}>
                      <MDTypography variant="caption" color="text" display="block">Designs</MDTypography>
                      <MDTypography variant="button" fontWeight="bold">{viewingCategory.designs?.length ?? 0}</MDTypography>
                    </Grid>
                    <Grid item xs={12}>
                      <MDTypography variant="caption" color="text" display="block">Slug</MDTypography>
                      <MDTypography variant="caption" sx={{ fontFamily: 'monospace', bgcolor: 'grey.100', px: 0.75, py: 0.25, borderRadius: 1 }}>
                        {viewingCategory.slug}
                      </MDTypography>
                    </Grid>
                    <Grid item xs={12}>
                      <MDTypography variant="caption" color="text" display="block">Created</MDTypography>
                      <MDTypography variant="caption">{moment(viewingCategory.createdAt).format('DD MMM YYYY')}</MDTypography>
                    </Grid>
                    {viewingCategory.heading && (
                      <Grid item xs={12}>
                        <MDTypography variant="caption" color="text" display="block">Hero Heading</MDTypography>
                        <MDTypography variant="body2" fontWeight="medium">{viewingCategory.heading}</MDTypography>
                      </Grid>
                    )}
                    {viewingCategory.subHeading && (
                      <Grid item xs={12}>
                        <MDTypography variant="caption" color="text" display="block">Hero Sub Heading</MDTypography>
                        <MDTypography variant="body2">{viewingCategory.subHeading}</MDTypography>
                      </Grid>
                    )}
                    {viewingCategory.description && (
                      <Grid item xs={12}>
                        <MDTypography variant="caption" color="text" display="block">Card Description</MDTypography>
                        <MDTypography variant="body2" sx={{ lineHeight: 1.6 }}>{viewingCategory.description}</MDTypography>
                      </Grid>
                    )}
                  </Grid>
                </Box>

              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton color="info" onClick={() => { setOpenViewDialog(false); handleEditCategory(viewingCategory?.id); }}>
            Edit Category
          </MDButton>
          <MDButton onClick={() => navigate(`/admin/designs/category/${viewingCategory?.id}`)}>
            View Designs
          </MDButton>
          <MDButton onClick={() => setOpenViewDialog(false)}>Close</MDButton>
        </DialogActions>
      </Dialog>

      {/* ===== CREATE / EDIT CATEGORY DIALOG ===== */}
      <Dialog open={openCategoryDialog} onClose={closeCategoryDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{selectedCategory ? `Edit — ${selectedCategory.name}` : 'Add New Category'}</Typography>
            <IconButton size="small" onClick={closeCategoryDialog}><Close /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmitCategory(handleCategorySubmit)}>

            <TextField
              label="Name *" fullWidth margin="normal"
              {...registerCategory('name', { required: 'Name required' })}
              error={!!categoryErrors.name} helperText={categoryErrors.name?.message}
            />
            <TextField
              label="Slug *" fullWidth margin="normal"
              {...registerCategory('slug', { required: 'Slug required' })}
              error={!!categoryErrors.slug}
              helperText={categoryErrors.slug?.message || 'Used in URLs, lowercase, no spaces'}
            />
            <TextField
              label="Description" multiline rows={3} fullWidth margin="normal"
              helperText="Short text shown under the category name on the main Designs page card"
              {...registerCategory('description')}
            />

            {/* ── Browse Page Hero Text ── */}
            <Divider sx={{ my: 1.5 }} />
            <MDTypography variant="button" fontWeight="medium" display="block" mb={0.5}>
              Browse Page Hero Text
            </MDTypography>
            <MDTypography variant="caption" color="text" display="block" mb={1.5} sx={{ fontSize: 11 }}>
              These appear centred over the hero image on the category browse page.
            </MDTypography>
            <TextField
              label="Heading" fullWidth margin="dense"
              placeholder="e.g. Logo Design Templates"
              helperText="Large H1 shown in the centre of the hero banner"
              {...registerCategory('heading')}
            />
            <TextField
              label="Sub Heading" fullWidth margin="dense"
              placeholder="e.g. Professional logos for every brand"
              helperText="Smaller subtitle under the heading"
              {...registerCategory('subHeading')}
            />
            <FormControlLabel
              sx={{ mt: 0.5, mb: 1 }}
              control={<Switch checked={isActiveCategory} onChange={(e) => setIsActiveCategory(e.target.checked)} color="info" />}
              label={<MDTypography variant="caption" fontWeight="medium">Active (visible to users)</MDTypography>}
            />

            {/* ===== THUMBNAIL IMAGE MANAGER ===== */}
            <Divider sx={{ my: 1.5 }} />
            <MDTypography variant="button" fontWeight="medium" display="block" mb={0.5}>
              Thumbnail Image
            </MDTypography>
            <MDTypography variant="caption" color="text" display="block" mb={1.5} sx={{ fontSize: 11 }}>
              Small square/portrait image shown on the main designs page category cards. Recommended: 400×500px.
            </MDTypography>

            {selectedCategory ? (
              <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
                {selectedCategory.thumbnail && keepThumbnail ? (
                  <Box>
                    <MDTypography variant="caption" color="text" fontWeight="medium" display="block" mb={1}>
                      Current thumbnail
                    </MDTypography>
                    <Box sx={{ position: 'relative', display: 'inline-block' }}>
                      <img
                        src={selectedCategory.thumbnail}
                        alt="Thumbnail"
                        style={{ width: 120, height: 150, objectFit: 'cover', borderRadius: 8, display: 'block', border: '2px solid #1976d2' }}
                      />
                      <Tooltip title="Remove thumbnail">
                        <Box
                          onClick={() => setKeepThumbnail(false)}
                          sx={{
                            position: 'absolute', top: -8, right: -8,
                            width: 24, height: 24, borderRadius: '50%',
                            bgcolor: 'error.main', display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', boxShadow: 2,
                          }}
                        >
                          <Close sx={{ fontSize: 14, color: '#fff' }} />
                        </Box>
                      </Tooltip>
                    </Box>
                    <MDTypography variant="caption" color="text" display="block" mt={1} sx={{ fontSize: 10 }}>
                      Click ✕ to remove, or upload below to replace.
                    </MDTypography>
                    <Box mt={1}>
                      <Input type="file" fullWidth inputProps={{ accept: 'image/*' }}
                        {...registerCategory('thumbnail')} />
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    <Box sx={{
                      height: 80, border: '2px dashed', borderColor: 'grey.300',
                      borderRadius: 2, display: 'flex', alignItems: 'center',
                      justifyContent: 'center', mb: 1.5, bgcolor: 'grey.50',
                    }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <ImageIcon sx={{ color: 'grey.400', fontSize: 24, display: 'block', mx: 'auto' }} />
                        <MDTypography variant="caption" color="text" sx={{ opacity: 0.6 }}>
                          {selectedCategory.thumbnail ? 'Thumbnail removed — upload a new one' : 'No thumbnail'}
                        </MDTypography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Box sx={{ flex: 1 }}>
                        <Input type="file" fullWidth inputProps={{ accept: 'image/*' }}
                          {...registerCategory('thumbnail')} />
                      </Box>
                      {selectedCategory.thumbnail && (
                        <MDButton size="small" color="secondary" variant="outlined"
                          onClick={() => setKeepThumbnail(true)}>
                          Restore
                        </MDButton>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            ) : (
              <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
                <MDTypography variant="caption" color="text" display="block" mb={0.75} sx={{ fontSize: 11 }}>
                  Shown as the card image on the main Designs page.
                </MDTypography>
                <Input type="file" fullWidth inputProps={{ accept: 'image/*' }}
                  {...registerCategory('thumbnail')} />
              </Box>
            )}

            {/* ===== HERO IMAGE MANAGER ===== */}
            <Divider sx={{ my: 1.5 }} />
            <MDTypography variant="button" fontWeight="medium" display="block" mb={1.5}>Hero Image</MDTypography>

            {selectedCategory ? (
              // EDIT MODE — show current image with option to replace
              <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
                {selectedCategory.heroImage && keepHeroImage ? (
                  <Box>
                    <MDTypography variant="caption" color="text" fontWeight="medium" display="block" mb={1}>
                      Current hero image
                    </MDTypography>
                    <Box sx={{ position: 'relative', display: 'inline-block' }}>
                      <img
                        src={selectedCategory.heroImage}
                        alt="Hero"
                        style={{
                          width: '100%', maxHeight: 160, objectFit: 'cover',
                          borderRadius: 8, display: 'block', border: '2px solid #1976d2',
                        }}
                      />
                      {/* Remove current image button */}
                      <Tooltip title="Remove this image (you can upload a new one)">
                        <Box
                          onClick={() => setKeepHeroImage(false)}
                          sx={{
                            position: 'absolute', top: -8, right: -8,
                            width: 24, height: 24, borderRadius: '50%',
                            bgcolor: 'error.main', display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', boxShadow: 2,
                          }}
                        >
                          <Close sx={{ fontSize: 14, color: '#fff' }} />
                        </Box>
                      </Tooltip>
                    </Box>
                    <MDTypography variant="caption" color="text" display="block" mt={1} sx={{ fontSize: 10 }}>
                      Click the red ✕ to remove, or upload below to replace it.
                    </MDTypography>
                    <Box mt={1}>
                      <MDTypography variant="caption" color="text" display="block" mb={0.5} sx={{ fontSize: 11 }}>
                        Upload new image to replace:
                      </MDTypography>
                      <Input type="file" fullWidth inputProps={{ accept: 'image/*' }}
                        {...registerCategory('heroImage')} />
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    <Box sx={{
                      height: 80, border: '2px dashed', borderColor: 'grey.300',
                      borderRadius: 2, display: 'flex', alignItems: 'center',
                      justifyContent: 'center', mb: 1.5, bgcolor: 'grey.50',
                    }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <ImageIcon sx={{ color: 'grey.400', fontSize: 24, display: 'block', mx: 'auto' }} />
                        <MDTypography variant="caption" color="text" sx={{ opacity: 0.6 }}>
                          {selectedCategory.heroImage ? 'Image removed — upload a new one' : 'No hero image'}
                        </MDTypography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Box sx={{ flex: 1 }}>
                        <Input type="file" fullWidth inputProps={{ accept: 'image/*' }}
                          {...registerCategory('heroImage')} />
                      </Box>
                      {selectedCategory.heroImage && (
                        <MDButton size="small" color="secondary" variant="outlined"
                          onClick={() => setKeepHeroImage(true)}>
                          Restore
                        </MDButton>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            ) : (
              // CREATE MODE — simple upload
              <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
                <MDTypography variant="caption" color="text" display="block" mb={0.75} sx={{ fontSize: 11 }}>
                  Recommended: 1200×400px or wider. Shown as banner on category page.
                </MDTypography>
                <Input type="file" fullWidth inputProps={{ accept: 'image/*' }}
                  {...registerCategory('heroImage')} />
              </Box>
            )}

            <DialogActions sx={{ mt: 2.5, px: 0 }}>
              <MDButton onClick={closeCategoryDialog}>Cancel</MDButton>
              <MDButton type="submit" color="info" variant="contained">
                {selectedCategory ? 'Update Category' : 'Create Category'}
              </MDButton>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>

      {/* ===== MANAGE FILTERS DIALOG ===== */}
      <Dialog open={openManageAttrsDialog} onClose={() => setOpenManageAttrsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6">Manage Filters</Typography>
              <Typography variant="caption" color="text.secondary">
                {managingCategory?.name} — filters appear in the sidebar when users browse this category
              </Typography>
            </Box>
            {canCreate && (
              <MDButton size="small" color="info" variant="contained" startIcon={<Add />}
                onClick={() => { setSelectedAttrType(null); resetAttrType({ type: 'enum', orderInFilter: 0 }); setOpenAttrTypeDialog(true); }}>
                Add Filter
              </MDButton>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {attributeTypes.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 5, border: '2px dashed', borderColor: 'grey.200', borderRadius: 2 }}>
              <Tune sx={{ fontSize: 40, color: 'grey.300', display: 'block', mx: 'auto', mb: 1 }} />
              <MDTypography variant="body2" color="text" display="block">No filters yet.</MDTypography>
              <MDTypography variant="caption" color="text" sx={{ opacity: 0.6 }}>
                Add filters like "Color", "Orientation", "Style" so users can narrow down designs.
              </MDTypography>
            </Box>
          ) : (
            attributeTypes.map((attrType) => (
              <Accordion key={attrType.id} sx={{ mb: 1, '&:before': { display: 'none' }, border: '1px solid', borderColor: 'grey.200', borderRadius: '8px !important' }} elevation={0}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', flexWrap: 'wrap' }}>
                    <Typography fontWeight="medium" sx={{ fontSize: 14 }}>{attrType.name}</Typography>
                    <Chip label={attrType.key} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: 11 }} />
                    <Chip label={attrType.type} size="small" color="info" />
                    <Chip label={`Order: ${attrType.orderInFilter}`} size="small" variant="outlined" />
                    {attrType.isFilterable && <Chip label="filterable" size="small" color="success" variant="outlined" />}
                    <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                      {canUpdate && (
                        <Tooltip title="Edit filter">
                          <IconButton size="small" color="info" onClick={(e) => {
                            e.stopPropagation();
                            setSelectedAttrType(attrType);
                            setValueAttrType('name', attrType.name);
                            setValueAttrType('key', attrType.key);
                            setValueAttrType('type', attrType.type);
                            setValueAttrType('isFilterable', attrType.isFilterable);
                            setValueAttrType('isSearchable', attrType.isSearchable);
                            setValueAttrType('orderInFilter', attrType.orderInFilter);
                            setOpenAttrTypeDialog(true);
                          }}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canDelete && (
                        <Tooltip title="Delete filter">
                          <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDeleteAttrType(attrType.id); }}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Divider sx={{ mb: 1.5 }} />
                  {(attrType.type === 'enum' || attrType.type === 'multi_enum') ? (
                    <Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
                        {attrType.options?.map((opt) => (
                          <Chip
                            key={opt.id}
                            label={`${opt.label} · ${opt.value}`}
                            size="small"
                            onDelete={() => handleDeleteOption(opt.id)}
                            sx={{ fontSize: 11 }}
                          />
                        ))}
                        {(!attrType.options || attrType.options.length === 0) && (
                          <MDTypography variant="caption" color="text" sx={{ opacity: 0.5 }}>
                            No options yet — add options below.
                          </MDTypography>
                        )}
                      </Box>
                      <MDButton size="small" variant="outlined" color="info" startIcon={<Add />}
                        onClick={() => handleOpenAddOptions(attrType)}>
                        Add Options
                      </MDButton>
                    </Box>
                  ) : (
                    <MDTypography variant="caption" color="text" sx={{ opacity: 0.6 }}>
                      Type <strong>{attrType.type}</strong> — no predefined options needed.
                      {attrType.type === 'boolean' && ' Admin selects Yes/No when tagging designs.'}
                      {attrType.type === 'number' && ' Admin enters a number when tagging designs.'}
                      {attrType.type === 'string' && ' Admin enters free text when tagging designs.'}
                    </MDTypography>
                  )}
                </AccordionDetails>
              </Accordion>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setOpenManageAttrsDialog(false)}>Close</MDButton>
        </DialogActions>
      </Dialog>

      {/* ===== ATTRIBUTE TYPE DIALOG ===== */}
      <Dialog open={openAttrTypeDialog} onClose={closeAttrTypeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{selectedAttrType ? 'Edit Filter' : 'Add New Filter'}</Typography>
            <IconButton size="small" onClick={closeAttrTypeDialog}><Close /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmitAttrType(handleAttrTypeSubmit)}>
            <TextField
              label="Display Name *" fullWidth margin="normal"
              placeholder="e.g. Color, Orientation, Style"
              {...registerAttrType('name', { required: 'Name required' })}
              error={!!attrTypeErrors.name} helperText={attrTypeErrors.name?.message}
            />
            {!selectedAttrType && (
              <>
                <TextField
                  label="Key *" fullWidth margin="normal"
                  placeholder="e.g. color, orientation (lowercase, underscores only)"
                  {...registerAttrType('key', {
                    required: 'Key required',
                    pattern: { value: /^[a-z_]+$/, message: 'Lowercase letters and underscores only' },
                  })}
                  error={!!attrTypeErrors.key}
                  helperText={attrTypeErrors.key?.message || 'Used in URL filter params — cannot be changed later'}
                />
                <FormControl fullWidth margin="normal">
                  <InputLabel>Type *</InputLabel>
                  <Select label="Type *" defaultValue="enum" {...registerAttrType('type', { required: true })}>
                    {ATTRIBUTE_TYPES.map(t => (
                      <MenuItem key={t} value={t}>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>{t}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t === 'enum' && 'Single choice from predefined options'}
                            {t === 'multi_enum' && 'Multiple choices from predefined options'}
                            {t === 'boolean' && 'Yes / No toggle'}
                            {t === 'number' && 'Numeric value'}
                            {t === 'string' && 'Free text'}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}
            <TextField
              label="Order in Filter Sidebar" type="number" fullWidth margin="normal"
              {...registerAttrType('orderInFilter')}
              helperText="Lower number = appears first (0, 1, 2...)"
            />
            <FormControlLabel
              control={<Switch defaultChecked {...registerAttrType('isSearchable')} color="info" />}
              label={<MDTypography variant="caption" fontWeight="medium">Include in search</MDTypography>}
            />
            <DialogActions sx={{ mt: 2, px: 0 }}>
              <MDButton onClick={closeAttrTypeDialog}>Cancel</MDButton>
              <MDButton type="submit" color="info" variant="contained">
                {selectedAttrType ? 'Update Filter' : 'Create Filter'}
              </MDButton>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>

      {/* ===== ADD OPTIONS DIALOG ===== */}
      <Dialog open={openAttrOptionDialog} onClose={() => setOpenAttrOptionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6">Add Options</Typography>
              <Typography variant="caption" color="text.secondary">
                Filter: {selectedAttrTypeForOptions?.name}
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setOpenAttrOptionDialog(false)}><Close /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <MDTypography variant="caption" color="text" mb={2} display="block">
            These are the choices users will see in the filter sidebar (e.g. Red, Blue, Green for a Color filter).
          </MDTypography>
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
            {attrOptions.map((opt, idx) => (
              <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                <TextField
                  label="Label" size="small" sx={{ flex: 2 }}
                  placeholder="e.g. Red"
                  value={opt.label}
                  onChange={(e) => {
                    handleOptionChange(idx, 'label', e.target.value);
                    if (!opt.value || opt.value === attrOptions[idx]?.label?.toLowerCase().replace(/\s+/g, '_')) {
                      handleOptionChange(idx, 'value', e.target.value.toLowerCase().replace(/\s+/g, '_'));
                    }
                  }}
                />
                <TextField
                  label="Value" size="small" sx={{ flex: 2 }}
                  placeholder="e.g. red"
                  value={opt.value}
                  onChange={(e) => handleOptionChange(idx, 'value', e.target.value)}
                />
                <TextField
                  label="#" size="small" type="number" sx={{ flex: 0.7 }}
                  value={opt.order}
                  onChange={(e) => handleOptionChange(idx, 'order', e.target.value)}
                />
                <IconButton size="small" color="error" onClick={() => handleRemoveOptionRow(idx)}
                  disabled={attrOptions.length === 1}>
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <MDButton size="small" startIcon={<Add />} onClick={handleAddOptionRow} sx={{ mt: 0.5 }}>
              Add Row
            </MDButton>
          </Paper>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setOpenAttrOptionDialog(false)}>Cancel</MDButton>
          <MDButton color="info" variant="contained" onClick={handleSubmitOptions}>
            Save Options
          </MDButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
};

export default Designs;
